import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'

import useText from '../../../../common/hooks/useText'
import { isFetchBaseQueryError } from '../../../../common/redux/api/fetchBaseQueryWithReauth'
import { useChargePointAddServicePartnerMutation } from '../../../../common/redux/api/web-api'
import { FieldInfoButton } from '../../../../common/uikit/field-info-button'
import { Input } from '../../../../common/uikit/Input'
import { Panel } from '../../../../common/uikit/Panel'
import { useToast } from '../../../../common/uikit/ToastProvider'

type ServicePartnerAccessWidgetProps = {
    chargePointId: string
    isAccessAvailable: boolean
}

type ServicePartnerAccessFormValues = {
    partnerReferenceCode: string
}

const getGrantErrorMessageKey = (error: unknown): string => {
    if (!isFetchBaseQueryError(error)) return 'msgServicePartnerAccessGrantFailed'

    switch (error.status) {
        case 403:
            return 'msgServicePartnerAccessUnavailable'
        case 404:
            return 'msgServicePartnerAccessInvalidCode'
        case 400:
            return 'msgServicePartnerAccessRequestInvalid'
        case 409:
            return 'msgServicePartnerAccessRequestRejected'
        case 410:
            return 'msgServicePartnerAccessCodeExpired'
        default:
            return 'msgServicePartnerAccessGrantFailed'
    }
}

export function ServicePartnerAccessWidget({
    chargePointId,
    isAccessAvailable,
}: ServicePartnerAccessWidgetProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const [isAuthorised, setIsAuthorised] = useState(false)
    const [grantServicePartnerAccess, { isLoading: isGranting }] = useChargePointAddServicePartnerMutation()
    const {
        handleSubmit,
        register,
        reset,
        setValue,
        formState: { errors, isSubmitted, isSubmitting, isValid },
    } = useForm<ServicePartnerAccessFormValues>({
        defaultValues: { partnerReferenceCode: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })
    const partnerReferenceCodeRegistration = register('partnerReferenceCode', {
        required: text('msgServicePartnerCodeRequired'),
        validate: (value) => value.trim().length === 8 || text('msgServicePartnerCodeInvalid'),
    })

    const grantAccess = async ({ partnerReferenceCode }: ServicePartnerAccessFormValues) => {
        if (!isAuthorised) return

        try {
            await grantServicePartnerAccess({
                chargePointId,
                addServicePartnerByCodeRequest: { code: partnerReferenceCode.trim() },
            }).unwrap()
            toast.notifySuccess(text('msgServicePartnerAccessGranted'))
            reset()
            setIsAuthorised(false)
        } catch (error) {
            toast.notifyError(text(getGrantErrorMessageKey(error)))
        }
    }

    return (
        <Panel
            title={text('lblServicePartnerAccessWidgetTitle')}
            headerIcon="pi pi-shield"
            headerActions={
                <FieldInfoButton
                    ariaLabel={text('lblMoreInformation')}
                    text={text('msgServicePartnerAccessInformation')}
                    testId="service-partner-access-info-button"
                />
            }
            className="mt-4"
            testId="service-partner-access-widget"
        >
            {!isAccessAvailable ? (
                <p className="m-0">{text('msgServicePartnerAccessUnavailable')}</p>
            ) : (
                <>
                    <p className="mt-0 text-color-secondary">{text('msgServicePartnerAccessWidgetDescription')}</p>

                    <form onSubmit={handleSubmit(grantAccess)} noValidate>
                        <label className="p-label required-label" htmlFor="service-partner-access-reference-code">
                            {text('lblServicePartnerReferenceCode')}
                        </label>
                        <Input
                            id="service-partner-access-reference-code"
                            data-testid="service-partner-access-reference-code"
                            {...partnerReferenceCodeRegistration}
                            onChange={(event) => {
                                setValue('partnerReferenceCode', event.target.value.toUpperCase(), {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: isSubmitted,
                                })
                                setIsAuthorised(false)
                            }}
                            error={errors.partnerReferenceCode?.message}
                            disabled={isGranting || isSubmitting}
                            placeholder="ABCDEFGH"
                            maxLength={8}
                            required
                        />

                        <div className="flex align-items-center gap-2 mt-4">
                            <Checkbox
                                inputId="service-partner-access-authorisation"
                                checked={isAuthorised}
                                onChange={(event) => setIsAuthorised(!!event.checked)}
                                disabled={isGranting || isSubmitting}
                            />
                            <label htmlFor="service-partner-access-authorisation" className="line-height-3">
                                {text('msgConfirmServicePartnerAccessAuthorised')}
                            </label>
                        </div>

                        <Button
                            type="submit"
                            label={text('btnGrantServicePartnerAccess')}
                            className="mt-3"
                            disabled={!isAuthorised || isGranting || isSubmitting || (isSubmitted && !isValid)}
                            loading={isGranting}
                        />
                    </form>
                </>
            )}
        </Panel>
    )
}
